import { HostInput, runProgram } from "idfweb-wasix";
import { readFile, type Vfs } from "idfweb-vfs";

import { EXECUTION_POLICIES, GUEST_USR, toolchainEnv, type Toolchain } from "./toolchain.ts";

/**
 * ESP-IDF's configuration server as a guest process: `python -m kconfserver`,
 * which esp-ice serves. It keeps an evaluated Kconfig and speaks kconfserver's
 * protocol, one JSON object per line on stdin and stdout.
 *
 * Its inputs are those of the kconfgen command in the build tree's
 * `menuconfig` target, which are the configure's. The tree's `confserver`
 * target leaves out IDF_MINIMAL_BUILD, and with a different Kconfig tree the
 * sdkconfig the server saves would change again at the next configure.
 */

export const CONFIG_SERVER = `${GUEST_USR}/bin/python3`;

export interface ConfigServer {
    /** Send one request, such as `{ version: 3, set: { NAME: value } }`. */
    send(request: object): void;
    /** Stop the server. */
    stop(): void;
    /** The exit code; -1 when it was stopped. */
    readonly exited: Promise<number>;
}

export interface ConfigServerOptions {
    /** Each reply, the status line first, as its JSON text. */
    onReply(reply: string): void;
    /** What the server logs on stderr. */
    onLog?: ((text: string) => void) | undefined;
}

/** How to run the server for a configured build directory. */
export interface ConfigServerCommand {
    /** The sdkconfig the server loads and saves. */
    sdkconfig: string;
    /** The arguments after `python -m kconfserver`. */
    args: string[];
}

/** kconfgen options that specify outputs or inputs kconfserver does not take, with their value count. */
const DROPPED: Record<string, number> = {
    "--defaults": 1,
    "--output": 2,
    "--menuconfig": 0,
    "--dont-write-deprecated": 0,
};

/**
 * The server's command for the build directory `buildDir`, from the
 * `menuconfig` target in its build.ninja; undefined before the first
 * configure.
 */
export function configServerCommand(vfs: Vfs, buildDir: string): ConfigServerCommand | undefined {
    let ninja: string;
    try {
        ninja = new TextDecoder().decode(readFile(vfs, `${buildDir}/build.ninja`));
    } catch {
        return undefined;
    }
    const edge = ninja.indexOf("\nbuild CMakeFiles/menuconfig ");
    const command = edge < 0 ? undefined : /\n {2}COMMAND = (.*)/.exec(ninja.slice(edge + 1))?.[1];
    if (command === undefined) return undefined;
    const words = shellWords(command.replace(/\$([$ :])/g, "$1"));
    const at = words.findIndex((word, i) => word === "kconfgen" && words[i - 1] === "-m");
    if (at < 0) return undefined;

    const args: string[] = [];
    // `cmake -E env NAME=VALUE ...` before the kconfgen call: its environment.
    const env = words.lastIndexOf("env", at);
    if (env > 0 && words[env - 1] === "-E") {
        for (const word of words.slice(env + 1, at - 2)) if (word.includes("=")) args.push("--env", word);
    }
    let sdkconfig: string | undefined;
    for (let i = at + 1; i < words.length && words[i] !== "&&"; i++) {
        const word = words[i]!;
        const [name] = word.split("=", 1);
        if (name! in DROPPED) {
            if (!word.includes("=")) i += DROPPED[name!]!;
        } else if (name === "--list-separator") {
            if (!word.includes("=")) i++;
        } else {
            if (word === "--config") sdkconfig = words[i + 1];
            args.push(word);
        }
    }
    return sdkconfig === undefined ? undefined : { sdkconfig, args };
}

/** Split a POSIX shell command line into words, with quotes and backslashes removed. */
function shellWords(line: string): string[] {
    const words: string[] = [];
    let word: string | undefined;
    for (let i = 0; i < line.length; i++) {
        const c = line[i]!;
        if (c === " " || c === "\t") {
            if (word !== undefined) words.push(word);
            word = undefined;
        } else if (c === "'") {
            const end = line.indexOf("'", i + 1);
            word = (word ?? "") + line.slice(i + 1, end < 0 ? undefined : end);
            i = end < 0 ? line.length : end;
        } else if (c === '"') {
            word ??= "";
            for (i++; i < line.length && line[i] !== '"'; i++) {
                if (line[i] === "\\" && i + 1 < line.length) i++;
                word += line[i];
            }
        } else if (c === "\\" && i + 1 < line.length) {
            word = (word ?? "") + line[++i];
        } else {
            word = (word ?? "") + c;
        }
    }
    if (word !== undefined) words.push(word);
    return words;
}

/**
 * Start the configuration server for the configured build directory
 * `buildDir`. Throws when the directory has not been configured.
 */
export function startConfigServer(tc: Toolchain, buildDir: string, opts: ConfigServerOptions): ConfigServer {
    const command = configServerCommand(tc.vfs, buildDir);
    if (command === undefined) throw new Error(`${buildDir} has not been configured; build the project first`);
    const input = new HostInput();
    const stop = new AbortController();
    const lines = new LineReader();
    const decoder = new TextDecoder();
    const run = runProgram({
        vfs: tc.vfs,
        program: CONFIG_SERVER,
        args: [CONFIG_SERVER, "-m", "kconfserver", ...command.args],
        env: toolchainEnv(),
        cwd: buildDir,
        preopens: tc.preopens,
        policies: EXECUTION_POLICIES,
        trace: tc.trace,
        stdin: input.pipe,
        signal: stop.signal,
        onStdout: (bytes) => {
            for (const line of lines.push(bytes)) opts.onReply(line);
        },
        onStderr: (bytes) => opts.onLog?.(decoder.decode(bytes, { stream: true })),
    });
    const exited = run.then(({ exitCode, failure }) => {
        if (failure !== undefined) opts.onLog?.(`${failure.message}\n`);
        return exitCode;
    });
    const encoder = new TextEncoder();
    return {
        send: (request) => input.write(encoder.encode(`${JSON.stringify(request)}\n`)),
        stop: () => {
            input.close();
            stop.abort();
        },
        exited,
    };
}

/** Splits a byte stream into its non-empty lines, however it arrives in chunks. */
export class LineReader {
    #pending = "";
    readonly #decoder = new TextDecoder();

    push(bytes: Uint8Array): string[] {
        const text = this.#pending + this.#decoder.decode(bytes, { stream: true });
        const lines = text.split("\n");
        this.#pending = lines.pop() ?? "";
        return lines.filter((line) => line.trim() !== "");
    }
}
